// The note text-atom contract — the whole wire format of a «Заметка».
// («Atom» here is a backend text atom, not a Jotai atom.)
//
// Described below as `scene_note`, the type it was written for; #1840-17 added
// `act_note` and `fragment_note` under the SAME contract, and every function
// here serves all three (`data/hostedNoteAtoms.ts` is their reader).
//
// A note persists as ONE text atom of type `scene_note` (backend migration
// `54696ac0`: textable = Scene, «Заметка Сцены», `max_qty` 100), written
// through the ordinary text-atom CRUD at `projects/:p/text-atoms`. Its body is
// the atom's `content`; everything else — identity, threading, quote,
// authorship — is `content_json`:
//
//     content      = note body
//     content_json = { uuid, parent, quote_head, quote_text,
//                      by: {user, at} | null, edited: {user, at} | null }
//
// The **uuid is client-minted and is the join key**: scene text carries only
// `[[note:<uuid>]]`, never the atom's server id, so ids may be assigned (and
// re-assigned, after an undo re-creates a deleted note) without ever rewriting
// scene text. Nothing server-side validates `content_json` — the column takes
// any object — which is exactly why this module is the only place that shape
// is spelled out.
//
// That the id is absent here is deliberate, not an omission: a note's identity
// is its uuid all the way down, so `NoteRecord` has no id field to hold one.
// The integer id is transport state — `PUT/DELETE …/text-atoms/:id` needs it,
// so whatever loads the atoms keeps a uuid → id index of its own (#1827).
//
// Contract facts this module encodes, each one a hazard if forgotten
// (findings from reading the API, issue #1826):
//
//   - **Two read shapes.** `TextAtomResource` (the `?editor` responses and
//     `editor/scenes/:episode/text-atoms`) decodes `content_json` into an
//     object; the plain atom endpoints return it as the raw JSON *string* it
//     is stored as. {@link fromSceneNoteAtom} accepts both.
//   - **Only the dedicated endpoint persists `content_json`.** The nested
//     `text_atoms[]` payload other screens use goes through a server-side
//     update that copies `content` and `order` only and silently drops
//     `content_json` — a note saved that way would lose its identity. Notes
//     must be written through `POST/PUT projects/:p/text-atoms/:id`.
//   - **The type id is not knowable ahead of the deploy.** Unlike every other
//     entry in the `textAtomTypeId` enum, `scene_note`'s id is whatever the
//     migration assigns, so it must be resolved by {@link SCENE_NOTE_ATOM_CODE}
//     from the text-atom dictionary rather than hard-coded.
//   - **Authorship is client-written.** `text_atoms` has no created_by /
//     updated_by columns, so `by` / `edited` are stamps this client writes. A
//     note with no stamp is authorless — never a note authored by `''`.
//   - **The cap is enforced server-side too**, and rejects with a message
//     rather than a code: see {@link isNoteCapRejection}.
//
// Transport lives elsewhere (issue #1827 swaps `data/notesStore.ts`'s dev
// localStorage stub for the real API). This module holds the format and the
// one failure the format defines, so both sides of that swap agree on both.

import { ApiError } from '@/api/apiUtils';
import { type NoteRecord, normalizeNoteRecord } from '@/utils/fountain/noteMarker';

/** The `text_atom_types.code` of the notes type. The numeric id behind it is
 *  assigned by the migration, so it is resolved from the dictionary. */
export const SCENE_NOTE_ATOM_CODE = 'scene_note';

/** The same contract, hung off an ACT and off a FRAGMENT («эпизод») — the two
 *  types issue #1840-17 added for the header rows on the sheet. Everything
 *  below takes all three: a note's wire format does not depend on what it is
 *  about, which is why {@link toSceneNoteAtom} / {@link fromSceneNoteAtom} are
 *  shared rather than copied (see `data/hostedNoteAtoms.ts`). */
export const ACT_NOTE_ATOM_CODE = 'act_note';
export const FRAGMENT_NOTE_ATOM_CODE = 'fragment_note';

/** Who touched a note and when, as it travels on the wire. */
export interface SceneNoteWireStamp {
    user: string;
    at: string;
    /** Ownership (#1836). On `by` the SERVER writes it from the authenticated
     *  user whatever the client sent, and keeps the stored value on update;
     *  on `edited` it is the last editor. Missing on notes written before
     *  ownership existed. */
    user_id?: number | null;
}

/** The `content_json` of a `scene_note` atom — everything about a note except
 *  its body. Snake_case throughout: this is the wire, not the client model. */
export interface SceneNoteContentJson {
    /** Client-minted uuid — the key `[[note:<uuid>]]` joins to. */
    uuid: string;
    /** The note this one replies to, `null` for a standalone note. */
    parent: string | null;
    /** RETIRED separate attribution line (issue #1829): the attribution is
     *  the citation's own first `>`-quoted line inside `quote_text` now.
     *  Kept in the contract shape — old payloads still carry one, and
     *  {@link fromSceneNoteAtom} folds it into the citation on read — but
     *  always written `null`. */
    quote_head: string | null;
    /** The citation, `>` markers included — attribution lines and quoted
     *  words, oldest message first. */
    quote_text: string;
    /** Creation stamp; `null` on an authorless (legacy-adopted) note. */
    by: SceneNoteWireStamp | null;
    /** Last-edit stamp; `null` when the note was never stamped. */
    edited: SceneNoteWireStamp | null;
}

/** The writable half of a `scene_note` atom — what a create or update sends. */
export interface SceneNoteAtomPayload {
    content: string;
    content_json: SceneNoteContentJson;
}

/** The readable half — as much of an atom as reading a note needs. `content_json`
 *  is `unknown` on purpose: which of the two read shapes arrives depends on the
 *  endpoint. */
export interface SceneNoteAtomLike {
    content?: string | null;
    content_json?: unknown;
    /** The atom's owner — the column `TextAtomPolicy` checks before a note is
     *  updated or deleted (#1836). Absent from payloads older than the field. */
    author_id?: number | null;
}

/** As much of an atom as TELLING a note from a scene body atom needs. Every
 *  field is optional because each endpoint sends a different subset — which is
 *  the whole reason {@link isSceneNoteAtom} has three clauses. */
export interface SceneNoteAtomIdentity {
    text_atom_type_id?: number;
    /** The eager-loaded `text_atom_types` row, where the endpoint sends one.
     *  Spelled out field by field rather than imported so this module stays the
     *  contract's only dependency — `code` is the part that matters. */
    type?: { id?: number; code?: string; name?: string } | null;
    content_json?: unknown;
}

/**
 * Whether an atom is a note — asked wherever scene text is assembled.
 *
 * It has to be asked at all because the backend's scene-body queries
 * **blacklist** atom types rather than whitelisting them
 * (`Scene::text_atoms_no_ll`, `ScenesPresentationController::atomsByEpisode`
 * exclude logline/pep/synopsis and pass everything else through), so a scene's
 * notes ride along in the very payload the editor turns into script text. The
 * client mirrored that blacklist, which means an unrecognised note atom does
 * not merely render — it can be picked as the scene's PRIMARY atom, and the
 * next scene save overwrites a note's body with the whole screenplay.
 *
 * It deliberately does NOT take the numeric type id. That id is assigned by
 * the migration's sequence and is only knowable by asking the dictionary at
 * runtime (see {@link SCENE_NOTE_ATOM_CODE}) — threading an async answer
 * through every scene-text call site would leave a window in which notes are
 * silently script. Both clauses below read the payload itself instead:
 *
 *   - `type.code` — `projects/:p/scenes?with=text_atoms` eager-loads the type
 *     row, and the code is stable across environments. It also catches a note
 *     whose `content_json` was lost, which is the case that matters most:
 *     unreadable as a note, but still not screenplay.
 *   - a `uuid` in `content_json` — for the payloads that send bare atoms with
 *     no type relation (`editor/scenes/:episode/text-atoms`). Safe because the
 *     only other `content_json` a scene atom carries is the body's line data
 *     (`paragraph_lines` / `draft_line_ids`), which has no uuid.
 *
 * The resolved id does have one job, at the other end: it is the server-side
 * filter that makes the notes read return notes and nothing else.
 */
export function isSceneNoteAtom(atom: SceneNoteAtomIdentity): boolean {
    if (atom.type?.code === SCENE_NOTE_ATOM_CODE) return true;
    const json = parseContentJson(atom.content_json);
    return json !== null && readString(json.uuid) !== '';
}

/** A note record as the atom that stores it. `quote_head` stays in the frozen
 *  contract shape but is written empty since issue #1829: the attribution is
 *  the citation's own first `>`-quoted line inside `quote_text` now, and old
 *  clients reading `quote_head: null` simply render no separate header. */
export function toSceneNoteAtom(record: NoteRecord): SceneNoteAtomPayload {
    return {
        content: record.text,
        content_json: {
            uuid: record.id,
            parent: record.parentId,
            quote_head: null,
            quote_text: record.quote,
            by: toWireStamp(record.author, record.createdAt, record.authorId),
            edited: toWireStamp(record.updatedBy, record.updatedAt, record.updatedById),
        },
    };
}

/**
 * The note record an atom carries, or `null` when the atom carries none —
 * unparseable `content_json`, or one with no uuid to join a marker by. A
 * uuid-less atom is not a broken note to repair: with no key, nothing in scene
 * text can ever point at it, so reading it as a note would resurrect a body
 * with no placement.
 *
 * The citation is brought to its canonical shape on the way in
 * (`normalizeNoteRecord`, issue #1829): a legacy separate `quote_head` folds
 * into `quote_text` as the citation's own first quoted line, depths and order
 * realign. Idempotent, so re-reading an already-canonical atom changes
 * nothing.
 */
export function fromSceneNoteAtom(atom: SceneNoteAtomLike): NoteRecord | null {
    const json = parseContentJson(atom.content_json);
    if (json === null) return null;

    const uuid = readString(json.uuid);
    if (uuid === '') return null;

    const by = readStamp(json.by);
    const edited = readStamp(json.edited);
    const legacyHead = readString(json.quote_head);

    return normalizeNoteRecord({
        id: uuid,
        parentId: emptyToNull(readString(json.parent)),
        author: emptyToNull(by.user),
        // The column is what the server enforces; the stamp inside
        // `content_json` only stands in for a payload that predates it.
        authorId: atom.author_id !== undefined ? atom.author_id : by.userId,
        createdAt: emptyToNull(by.at),
        updatedBy: emptyToNull(edited.user),
        updatedById: edited.userId,
        updatedAt: emptyToNull(edited.at),
        ...(legacyHead === '' ? {} : { quoteHead: legacyHead }),
        quote: readString(json.quote_text),
        text: atom.content ?? '',
    });
}

/**
 * Whether a failed write is the server refusing note #101 for a scene.
 *
 * `Textable::checkCanStoreNewTextAtom` throws a plain `InvalidArgumentException`
 * carrying no code, which the text-atom controllers catch and render as **HTTP
 * 400**, body `{success: false, message: "Can't store more than 100 TextAtoms
 * with TextAtomType #<id>"}`. So the message is the only signal, and the count
 * is left open because `max_qty` is a column the backend may raise.
 *
 * It is deliberately every 4xx and not just 400: the one change asked of the
 * backend here is a stabler status/code, and this should keep working through
 * it. 5xx is deliberately excluded — the cap only reaches the client as a
 * server error if the write went through the nested `text_atoms[]` path, which
 * drops `content_json` and must never carry a note anyway. That is a bug to
 * fix at the call site, not a rejection to absorb.
 *
 * The client guards the cap itself before writing, so this only fires on a
 * race (two authors filling the last slot) — it exists to turn that race into
 * the same «лимит заметок» snackbar rather than a generic failure.
 */
export function isNoteCapRejection(error: unknown): boolean {
    if (!(error instanceof ApiError)) return false;
    if (error.status < 400 || error.status >= 500) return false;
    return /store more than \d+ TextAtoms/i.test(error.data?.message ?? '');
}

function toWireStamp(user: string | null, at: string | null, userId: number | null | undefined): SceneNoteWireStamp | null {
    if (user === null && at === null) return null;
    // `user_id` travels only when known: a legacy stamp must not gain a
    // `null` the server would then treat as an explicit value.
    return { user: user ?? '', at: at ?? '', ...(userId === null || userId === undefined ? {} : { user_id: userId }) };
}

/** Both read shapes, and every way either can be malformed, in one place.
 *
 *  Either shape can also arrive ARRAY-wrapped (`[{…}]`, and `[]` for an empty
 *  object — PHP's json_encode of an empty associative array): that is how the
 *  act / fragment endpoints hand `content_json` back, and #1840-18 reads notes
 *  off those very payloads. A reader that saw nothing there would turn a
 *  perfectly good note into a uuid-less orphan, so the unwrap is load-bearing —
 *  the same one `data/textAtomDraft.ts` and `collectContentJsonLineIds` carry. */
function parseContentJson(raw: unknown): Record<string, unknown> | null {
    if (typeof raw === 'string') {
        try {
            return unwrap(JSON.parse(raw));
        } catch {
            return null;
        }
    }
    return unwrap(raw);
}

function unwrap(value: unknown): Record<string, unknown> | null {
    return asRecord(Array.isArray(value) ? value[0] : value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function readString(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

function readStamp(value: unknown): { user: string; at: string; userId: number | null } {
    const record = asRecord(value);
    if (record === null) return { user: '', at: '', userId: null };
    return { user: readString(record.user), at: readString(record.at), userId: readUserId(record.user_id) };
}

/** The server writes `user_id` as a number; a numeric string is tolerated
 *  (Postgres JSON round-trips have done stranger things), anything else is
 *  "unknown". */
function readUserId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isInteger(value)) return value;
    if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
    return null;
}

/** `''` is how the wire spells "unknown" for every optional string; the client
 *  model spells it `null`. */
function emptyToNull(value: string): string | null {
    return value === '' ? null : value;
}
